import { useEffect, useState } from "react";
import { useIch, useNutzungSetzen } from "../api/abfragen";
import { hinweisGezeigt, hinweisMerken, hinweisOffenMelden } from "../api/nutzungHinweis";
import { Knopf } from "../design/Knopf";
import { Fehlertext } from "../design/Leer";
import { Schalter } from "../design/Schalter";
import { Sheet } from "../design/Sheet";
import { useToastSichtbar } from "../design/toastKontext";
import { useSichern } from "../design/sichern";
import { NUTZUNG_LINK, RechtLink } from "./rechtliches/Rechtliches";

/** W77 (D-292, D-304): einmaliger Hinweis zur Nutzungsstatistik fuer Konten von vor dem Haekchen. Die Schale zeigt ihn,
 *  wenn `ich` den Hinweis offen meldet und die Statistik an ist, einmal je Sitzung nach der ersten Seite (Anmeldung,
 *  Einladung, Einfuehrung und neue Fassung liegen ausserhalb der Schale). „Wahl speichern“ setzt die Wahl und schliesst den
 *  Hinweis beim Server; Schliessen ohne Speichern sendet nichts, der Hinweis bleibt offen und kommt beim naechsten Start. */
export function NutzungHinweis() {
  const { data: ich } = useIch();
  const faellig = Boolean(ich?.nutzer.nutzung_hinweis_offen && !ich.nutzer.nutzung_aus);
  const [offen, setOffen] = useState(false);
  const [an, setAn] = useState(true);
  const setzen = useNutzungSetzen();
  const { laeuft, sichern } = useSichern(offen);
  // W73d (D-314 H1): nicht ueber einer Meldung („Angemeldet als …“ mit „Abmelden“, 9 s); erst wenn keine mehr steht
  const toastSichtbar = useToastSichtbar();

  // erst nach dem ersten Bild der Seite; der Merker erst im Takt (beim Oeffnen), sonst verbraucht ihn das Probe-Aushaengen
  // von StrictMode oder eine Meldung, die das Oeffnen verschiebt
  useEffect(() => {
    if (!faellig || toastSichtbar || hinweisGezeigt()) return;
    const takt = setTimeout(() => {
      hinweisMerken();
      setOffen(true);
    }, 0);
    return () => clearTimeout(takt);
  }, [faellig, toastSichtbar]);
  // W81 B5: das Blatt zur neuen Fassung der Einwilligung kommt erst danach
  useEffect(() => hinweisOffenMelden(offen), [offen]);
  useEffect(() => () => hinweisOffenMelden(false), []);

  function speichern() {
    if (laeuft) return;
    setzen.reset();
    // W82 Web-5h1 (K47, BS30): Funktionsform, nach SICHERN_GRENZE_MS ohne Antwort bricht `useSichern` ab
    sichern((signal) => setzen.mutateAsync({ aus: !an, signal }, { onSuccess: () => setOffen(false) }));
  }

  return (
    <Sheet offen={offen} onSchliessen={() => setOffen(false)} titel="Nutzungsstatistik" gesperrt={laeuft}>
      <div className="mt-2 space-y-2 px-4">
        <p className="t-body hyphens-auto">Mit eingeschalteter Nutzungsstatistik zählen wir Bildschirme, Funktionen und Fehler, ohne Inhalte.</p>
        <p className="t-body hyphens-auto text-pg-text-2">
          Du kannst sie hier ausschalten und mit „Wahl speichern“ bestätigen oder jederzeit in den Einstellungen abschalten.
        </p>
        <p className="t-body hyphens-auto text-pg-text-2">Ohne gespeicherte Änderung bleibt deine bisherige Einstellung bestehen.</p>
      </div>
      {/* ohne eigenen Kasten und Innenrand: sonst blieben bei 320 px und 200 % unter 90 px fuer das Wort */}
      <div className="mt-4">
        <Schalter an={an} onChange={setAn} disabled={laeuft} autoFocus>
          {/* das lange Wort trennt bei grosser Schrift, statt ueber das Blatt zu ragen (wie Einstellungen, W65) */}
          <span className="hyphens-auto [overflow-wrap:anywhere]">Nutzungsstatistik</span>
        </Schalter>
      </div>
      {setzen.isError && <Fehlertext fehler={setzen.error} />}
      <Knopf gross breit className="mt-6" disabled={laeuft} onClick={speichern}>
        Wahl speichern
      </Knopf>
      <p className="mt-2 flex flex-col px-4">
        <RechtLink pfad="/datenschutz" text={NUTZUNG_LINK} />
      </p>
    </Sheet>
  );
}
